<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import { computed, onBeforeUnmount, onMounted, shallowRef } from 'vue';
import { captureUpsell } from '@/services/telemetry/posthog';
import { canShareExtra, type ExtraFile, prepareExtraFile } from './extra-files';

const props = defineProps<{
  orderId: string;
  sessionId?: string;
  analytics?: Record<string, unknown>;
  file: ExtraFile;
  poster?: string;
}>();
const prepared = shallowRef<File | null>(null);
const preparingShare = shallowRef(false);
const preparationFailed = shallowRef(false);
const shareError = shallowRef(false);
const controller = new AbortController();
function track(event: string, details: Record<string, unknown> = {}): void {
  if (props.sessionId) {
    captureUpsell(event, props.sessionId, {
      ...props.analytics,
      extra_order_id: props.orderId,
      file_id: props.file.id,
      mime_type: props.file.mime_type,
      ...details,
    });
  }
}
let played = false;
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
function playbackStarted(): void {
  if (played) {
    return;
  }
  played = true;
  track('extras_customer_video_started', { autoplay });
}
const supportsSharing =
  typeof navigator.share === 'function' &&
  typeof navigator.canShare === 'function';
const autoplay = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const shareable = computed(
  () => prepared.value && canShareExtra(prepared.value)
);
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const metadata = computed(
  () => `${props.file.width} × ${props.file.height} · MP4`
);

async function prepareShare(): Promise<void> {
  if (!supportsSharing || preparingShare.value || controller.signal.aborted) {
    return;
  }
  preparingShare.value = true;
  preparationFailed.value = false;
  shareError.value = false;
  try {
    const file = await prepareExtraFile(props.file, controller.signal);
    if (!controller.signal.aborted) {
      prepared.value = file;
    }
  } catch (error) {
    if (!controller.signal.aborted) {
      preparationFailed.value = true;
      captureFileError(error, 'file_preload');
    }
  } finally {
    preparingShare.value = false;
  }
}
onMounted(prepareShare);
onBeforeUnmount(() => {
  controller.abort();
  prepared.value = null;
});
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
function share(): void {
  if (!(prepared.value && shareable.value)) {
    return;
  }
  shareError.value = false;
  track('extras_share_requested');
  const sessionId = props.sessionId;
  const properties = {
    ...props.analytics,
    extra_order_id: props.orderId,
    file_id: props.file.id,
  };
  navigator
    .share({ files: [prepared.value] })
    .then(() => {
      if (sessionId) {
        captureUpsell('extras_share_resolved', sessionId, properties);
      }
    })
    .catch((error: Error) => {
      if (sessionId) {
        captureUpsell(
          error.name === 'AbortError'
            ? 'extras_share_cancelled'
            : 'extras_share_failed',
          sessionId,
          properties
        );
      }
      if (error.name !== 'AbortError') {
        shareError.value = true;
        captureFileError(error, 'share');
      }
    });
}

function captureFileError(error: unknown, stage: string): void {
  track('extras_customer_file_failed', { stage });
  Sentry.captureException(error, {
    tags: {
      feature: 'digital_extras',
      stage,
      extra_order_id: props.orderId,
      file_id: props.file.id,
    },
  });
}

// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
function playbackError(event: Event): void {
  const error = (event.target as HTMLVideoElement).error;
  if (error && error.code !== 1) {
    captureFileError(
      new Error(`Extra video playback failed (${error.code})`),
      'playback'
    );
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
function downloadRequested(): void {
  track('extras_download_requested');
  Sentry.logger.info('Digital extras: download.requested', {
    feature: 'digital_extras',
    stage: 'download',
    'extras.event': 'download.requested',
    extra_order_id: props.orderId,
    file_id: props.file.id,
  });
}
</script>

<template>
  <article class="ExtraFile">
    <div class="ExtraFile__preview" :style="{ aspectRatio: `${file.width} / ${file.height}` }">
      <video class="sentry-block" :src="file.url" :poster="poster" :autoplay="autoplay" muted controls playsinline loop preload="metadata" :aria-label="$t('CustomerDigitalExtras.video')" @error="playbackError" @playing="playbackStarted" />
    </div>
    <div class="ExtraFile__copy">
      <h2 class="ExtraFile__title">{{ $t('CustomerDigitalExtras.video') }}</h2>
      <p class="ExtraFile__description">{{ $t('CustomerDigitalExtras.videoDescription') }}</p>
      <p class="ExtraFile__meta">{{ metadata }}</p>
      <div class="ExtraFile__actions">
        <a class="kiosk-button-inline" :href="file.download_url" :download="file.filename" :aria-label="`${$t('CustomerDigitalExtras.download')}: ${$t('CustomerDigitalExtras.video')}`" @click="downloadRequested">
          {{ $t('CustomerDigitalExtras.download') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 3v12m-4-4 4 4 4-4M5 17v4h14v-4" /></svg>
        </a>
        <button
          v-if="supportsSharing && (!prepared || shareable)"
          type="button"
          :disabled="preparingShare"
          :aria-busy="preparingShare"
          :aria-label="`${$t(preparationFailed ? 'CustomerDigitalExtras.retry' : 'CustomerDigitalExtras.share')}: ${$t('CustomerDigitalExtras.video')}`"
          @click="prepared ? share() : prepareShare()"
        >
          <template v-if="preparingShare">{{ $t('CustomerDigitalExtras.preparingShare') }}</template>
          <template v-else-if="preparationFailed">{{ $t('CustomerDigitalExtras.retry') }}</template>
          <template v-else>{{ $t('CustomerDigitalExtras.share') }}</template>
        </button>
      </div>
      <p v-if="preparationFailed || shareError" role="status">{{ $t('CustomerDigitalExtras.shareFailed') }}</p>
    </div>
  </article>
</template>

<style scoped>
.ExtraFile { min-width: 0; overflow: hidden; border: 1px solid var(--Extras-line); border-radius: 20px; background: var(--Extras-panel); }
.ExtraFile__preview { position: relative; overflow: hidden; background: var(--Extras-surface-strong); }
.ExtraFile__preview video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; }
.ExtraFile__copy { padding: 18px; }
.ExtraFile .ExtraFile__title { margin: 0; font: 800 25px / 1.1 var(--Download-display-font); font-variation-settings: 'wght' 800; letter-spacing: -.7px; }
.ExtraFile p { margin: 8px 0 0; font-size: 13px; line-height: 1.6; }
.ExtraFile__description, .ExtraFile__meta { color: var(--Extras-muted); }
.ExtraFile .ExtraFile__meta { font-size: 11px; }
.ExtraFile__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 14px; }
.ExtraFile__actions :is(a, button) { display: inline-flex; justify-content: center; align-items: center; gap: 10px; min-height: 46px; padding: 12px 18px; border: 1px solid transparent; border-radius: 99px; font: 700 13px / 1.3 var(--Download-body-font); cursor: pointer; text-decoration: none; background: var(--Extras-accent); color: var(--Extras-on-accent); }
.ExtraFile__actions svg { width: 18px; height: 18px; flex: none; }
.ExtraFile__actions a { width: auto; height: auto; white-space: normal; text-transform: none; letter-spacing: 0; font-variation-settings: 'wght' 700; }
.ExtraFile__actions button { border-color: color-mix(in srgb, var(--Extras-secondary) 65%, transparent); background: var(--Extras-surface); color: var(--Extras-text); }
.ExtraFile__actions button:disabled { opacity: .6; cursor: wait; }
.ExtraFile__actions :is(a, button):focus-visible { outline: 3px solid var(--Extras-accent); outline-offset: 3px; }
</style>
